import { useCallback, useEffect, useRef, useState } from 'react';

/** `copied`/`failed` are transient — both fall back to `idle` on their own. */
export type CopyStatus = 'idle' | 'copied' | 'failed';

export interface UseCopyToClipboard {
  status: CopyStatus;
  /** Resolves to whether the write actually happened. */
  copy: (text: string) => Promise<boolean>;
}

/** Long enough to read the confirmation, short enough not to look stuck. */
const RESET_AFTER_MS = 3000;

/**
 * Copy, with the refusal reported instead of swallowed.
 *
 * `navigator.clipboard` is **absent** on an insecure origin (a staff portal
 * reached over plain http on a LAN is a real deployment) and **rejects** when the
 * document is not focused or the permission was denied. A control that shows
 * "copied" in either case has lied about the operator's data — so both paths end
 * in `failed`, and the caller says so out loud.
 *
 * There is deliberately no `document.execCommand('copy')` fallback: it is
 * deprecated, it requires a live selection this control does not own, and it
 * fails silently in exactly the situations the modern API refuses in.
 */
export function useCopyToClipboard(): UseCopyToClipboard {
  const [status, setStatus] = useState<CopyStatus>('idle');
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;

    return () => {
      isMounted.current = false;
      if (timer.current !== null) clearTimeout(timer.current);
    };
  }, []);

  const settle = useCallback((next: CopyStatus) => {
    if (timer.current !== null) clearTimeout(timer.current);
    setStatus(next);
    timer.current = setTimeout(() => {
      // The timer outlives the component that scheduled it; without this guard
      // the reset lands on an unmounted hook.
      if (isMounted.current) setStatus('idle');
    }, RESET_AFTER_MS);
  }, []);

  const copy = useCallback(
    async (text: string): Promise<boolean> => {
      try {
        await navigator.clipboard.writeText(text);
        settle('copied');
        return true;
      } catch {
        settle('failed');
        return false;
      }
    },
    [settle],
  );

  return { status, copy };
}
